/**
 * De groepjes van een groepsles (stap 23), in het voorbeeld van de les:
 * per groepje de kinderen, met hun niveau en het deel dat ze lezen. De
 * leerkracht verplaatst een kind naar een ander groepje of een ander deel
 * (het deel alleen zolang het kind nog niet begon), of laat de app de hele
 * groep opnieuw indelen zolang nog niemand begon.
 *
 * Daaronder wat de leerkracht nodig heeft voor het gesprek in de groepjes
 * (op papier): bij een verhaal het deel dat hij zelf vertelt, en de
 * modelreconstructie en de eindvraag (services/groepsles.py).
 */
import React, { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { leerkrachtApi as api } from '../../../api/leerkracht';
import { Draaier, Laden } from '../../../components/Laden';

const cel = { padding: '4px 8px', fontSize: 13 };

export function Groepjes({ les, meldFout }) {
  const [data, setData] = useState(null);
  const [bezig, setBezig] = useState(false);

  const laad = async () => {
    try {
      setData(await api.haalGroepjes(les.id));
    } catch (err) {
      meldFout(err.message);
    }
  };

  useEffect(() => {
    laad();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij het openen
  }, [les.id]);

  const doe = async (actie) => {
    setBezig(true);
    try {
      await actie();
      await laad();
    } catch (err) {
      meldFout(err.message);
    } finally {
      setBezig(false);
    }
  };

  const verplaats = (lid, plek) => doe(() => api.zetPlek(les.id, lid.student_id, plek));
  const opnieuw = () => {
    if (!window.confirm('De app deelt de hele groep opnieuw in zoals hij voorstelt; wat u veranderde, gaat verloren. Doorgaan?')) return;
    doe(() => api.deelOpnieuwIn(les.id));
  };

  if (!data) return <Laden tekst="Groepjes laden..." />;

  const teLezen = data.delen.filter(d => !d.verteld);
  // de titel van een deel erbij, als die meer zegt dan "Deel 2"
  const naam = (d) => `Deel ${d.nummer}${d.titel && d.titel.trim().toLowerCase() !== `deel ${d.nummer}` ? ` — ${d.titel}` : ''}`;
  const hoogste = Math.max(0, ...data.groepjes.map(g => g.nummer));
  const groepjesKeuze = Array.from({ length: hoogste + 1 }, (_, i) => i + 1);
  const opzet = data.opzet || {};

  return (
    <div data-testid="groepjes">
      <div className="flex-between mb-8">
        <h3>Groepjes</h3>
        <button className="btn btn-secondary" onClick={opnieuw} disabled={bezig || !data.kan_opnieuw}
          title={data.kan_opnieuw ? 'Terug naar de indeling die de app voorstelt' : 'Er zijn al kinderen begonnen; verplaats ze een voor een'}>
          {bezig ? <Draaier /> : <RefreshCw size={14} />} Opnieuw indelen
        </button>
      </div>
      <p className="text-muted text-small mb-8">
        Elk kind leest één deel, op zijn eigen niveau, en vertelt daarna in zijn groepje wat erin stond. Samen maken ze er
        één verhaal van (op papier). Kinderen van verschillende niveaus zitten bij elkaar; in een groepje van vijf lezen
        de twee met het laagste niveau samen een deel.
      </p>
      {data.waarschuwingen.length > 0 && (
        <div className="alert mb-8" style={{ background: 'var(--waarschuw-vlak)', color: 'var(--waarschuw-tekst)' }}
          data-testid="groepjes-let-op">
          {data.waarschuwingen.map((w, i) => <div key={i}>{w}</div>)}
        </div>
      )}
      <div style={{ maxHeight: 500, overflow: 'auto' }}>
        {data.groepjes.map(g => (
          <div key={g.nummer} className="card mb-8" style={{ padding: '8px 12px' }} data-testid={`groepje-${g.nummer}`}>
            <strong>Groepje {g.nummer}</strong> <span className="text-muted text-small">({g.leden.length} kinderen)</span>
            <table style={{ marginTop: 4 }}>
              <tbody>
                {g.leden.map(lid => (
                  <tr key={lid.student_id}>
                    <td style={cel}>{lid.naam}</td>
                    <td style={cel}><span className="badge badge-primary">{lid.niveau}</span></td>
                    <td style={cel}>
                      <select className="input" style={{ padding: '2px 6px', fontSize: 13 }} value={lid.deel || ''}
                        aria-label={`Deel van ${lid.naam}`} disabled={bezig || lid.begonnen}
                        title={lid.begonnen ? 'Begonnen met lezen: het deel ligt vast' : ''}
                        onChange={e => verplaats(lid, { deel: parseInt(e.target.value) })}>
                        {teLezen.map(d => <option key={d.nummer} value={d.nummer}>{naam(d)}</option>)}
                      </select>
                    </td>
                    <td style={cel}>
                      <select className="input" style={{ padding: '2px 6px', fontSize: 13 }} value={g.nummer}
                        aria-label={`Groepje van ${lid.naam}`} disabled={bezig}
                        onChange={e => verplaats(lid, { groepje: parseInt(e.target.value) })}>
                        {groepjesKeuze.map(n => (
                          <option key={n} value={n}>{n > hoogste ? `Nieuw groepje (${n})` : `Groepje ${n}`}</option>
                        ))}
                      </select>
                    </td>
                    <td style={cel}>
                      {lid.status === 'afgerond' ? <span className="badge badge-success">klaar</span>
                        : lid.begonnen ? <span className="badge badge-warning">leest</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {data.verteld && (
        <details className="card mb-8" style={{ padding: '8px 12px', background: 'var(--blauw-vlak)' }} data-testid="verteld">
          <summary><strong>U vertelt zelf: {naam(data.verteld)}</strong> (aan de hele groep, vóór of na het lezen)</summary>
          <p className="mt-8" style={{ lineHeight: 1.6 }}>{data.verteld.tekst}</p>
          {opzet.variant_vier && <p className="text-muted text-small mt-8">Uit de opzet: {opzet.variant_vier}</p>}
        </details>
      )}
      {(opzet.reconstructie?.length > 0 || opzet.eindvraag) && (
        <details className="card" style={{ padding: '8px 12px' }} data-testid="reconstructie">
          <summary><strong>Modelreconstructie en eindvraag</strong> (voor u; de kinderen zien dit niet)</summary>
          <ol style={{ margin: '8px 0 0 20px', lineHeight: 1.6, fontSize: 14 }}>
            {(opzet.reconstructie || []).map((r, i) => (
              <li key={i}>{r.zin} {r.bronnen?.length > 0 && <span className="text-muted">[{r.bronnen.join(' + ')}]</span>}</li>
            ))}
          </ol>
          {opzet.eindvraag && <p className="mt-8"><strong>Eindvraag:</strong> {opzet.eindvraag}</p>}
        </details>
      )}
    </div>
  );
}
